import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { NotificationService } from '../../shared/services/notification.service';

@Component({
  selector: 'app-proforma-status',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './proforma-status.component.html',
  styleUrl: './proforma-status.component.scss',
  animations: [
    trigger('slideInFromTop', [
      state(
        'void',
        style({
          transform: 'translateY(-40px)',
          opacity: 0,
        }),
      ),
      state(
        'visible',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      transition('void => visible', [animate('0.6s ease-out')]),
    ]),
    trigger('slideInFromBottom', [
      state(
        'void',
        style({
          transform: 'translateY(40px)',
          opacity: 0,
        }),
      ),
      state(
        'visible',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      transition('void => visible', [animate('0.6s ease-out')]),
    ]),
    trigger('fadeIn', [
      state(
        'void',
        style({
          opacity: 0,
        }),
      ),
      state(
        'visible',
        style({
          opacity: 1,
        }),
      ),
      transition('void => visible', [animate('0.5s ease-out')]),
    ]),
  ],
})
export class ProformaStatusComponent implements OnInit, OnDestroy {
  // Animation states
  confirmationVisible = false;
  detailsVisible = false;
  buttonsVisible = false;

  // URL / Route parameters
  success: boolean = false;
  orderId: string = '';
  paymentType: string = '';
  statusMessage: string = '';

  // Order & Proforma information
  bookingNumber: string = '';
  invoiceId: string = '';
  orderNumber: string = '';
  paymentAmount: number = 0;
  items: any[] = [];
  orderDetail: any = null;
  deliveryAddress: any = null;
  userData: any = null;
  transactionDate: Date = new Date();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);

    // Read user data from localStorage
    try {
      const userDataString = localStorage.getItem('userData1');
      if (userDataString && !userDataString.startsWith('U2FsdGVkX1')) {
        this.userData = JSON.parse(userDataString);
      }
    } catch (e) {
      this.userData = null;
    }

    // Parse URL query parameters
    this.route.queryParams.subscribe((params) => {
      this.success = params['success'] === 'true' || params['status'] === 'success';
      this.orderId = params['order_id'] || params['order_number'] || '';
      this.paymentType = params['payment_type'] || 'cc_avenue';
      this.invoiceId = params['invoice_id'] || this.orderId;

      this.loadPaymentContext();
      this.fetchProformaDetails();
    });

    // Staggered animations
    setTimeout(() => {
      this.confirmationVisible = true;
    }, 50);

    setTimeout(() => {
      this.detailsVisible = true;
    }, 250);

    setTimeout(() => {
      this.buttonsVisible = true;
    }, 450);
  }

  ngOnDestroy(): void {
    // Clean up temporary proforma context after successful viewing
    if (this.success) {
      localStorage.removeItem('proformaPaymentContext');
    }
  }

  private loadPaymentContext(): void {
    try {
      const storedContext = localStorage.getItem('proformaPaymentContext');
      if (storedContext) {
        const parsed = JSON.parse(storedContext);
        if (parsed) {
          if (parsed.orderId) {
            this.orderNumber = String(parsed.orderId);
          }
          if (parsed.invoiceId) {
            this.invoiceId = String(parsed.invoiceId);
          }
          if (parsed.bookingNumber) {
            this.bookingNumber = parsed.bookingNumber;
          }
          if (parsed.paymentAmount !== undefined) {
            this.paymentAmount = Number(parsed.paymentAmount) || 0;
          }
          if (Array.isArray(parsed.items) && parsed.items.length > 0) {
            this.items = parsed.items;
          }
          if (parsed.orderDetail) {
            this.orderDetail = parsed.orderDetail;
            this.deliveryAddress =
              parsed.orderDetail?.shipping_address ||
              parsed.orderDetail?.address ||
              null;
          }
        }
      }
    } catch (e) {
      console.warn('Could not read proformaPaymentContext:', e);
    }

    if (!this.orderNumber && this.orderId) {
      this.orderNumber = this.orderId;
    }
  }

  private fetchProformaDetails(): void {
    const identifier = this.bookingNumber || this.orderNumber || this.orderId;
    if (!identifier) return;

    this.loaderService.show();

    // 1. Try getPendingBookingAmount if we have a booking number or order number
    const bookingPayload = this.bookingNumber
      ? { booking_number: this.bookingNumber }
      : { order_number: this.orderNumber || this.orderId };

    this.apiService.getPendingBookingAmount(bookingPayload).subscribe({
      next: (res: any) => {
        if (res?.success && Array.isArray(res?.data) && res.data.length > 0) {
          const matched =
            res.data.find(
              (inv: any) =>
                String(inv.invoice_id) === String(this.orderId) ||
                String(inv.invoice_id) === String(this.invoiceId),
            ) || res.data[0];

          if (matched) {
            if (!this.bookingNumber && matched.booking_number) {
              this.bookingNumber = matched.booking_number;
            }
            if (!this.paymentAmount && matched.payment_amount) {
              this.paymentAmount = Number(matched.payment_amount);
            }
            if ((!this.items || this.items.length === 0) && Array.isArray(matched.part_details)) {
              this.items = matched.part_details;
            }
          }
          this.loaderService.hide();
        } else {
          this.fetchOrderDetailFallback();
        }
      },
      error: () => {
        this.fetchOrderDetailFallback();
      },
    });
  }

  private fetchOrderDetailFallback(): void {
    const targetOrder = this.orderNumber || this.orderId;
    if (!targetOrder) {
      this.loaderService.hide();
      return;
    }

    this.apiService
      .getorderlist({ order_number: targetOrder, action_type: 'invoice_list' })
      .subscribe({
        next: (orderListRes: any) => {
          if (orderListRes?.success && orderListRes.data) {
            this.orderDetail = orderListRes.data;
            this.extractFromOrderDetail(orderListRes.data);
            this.loaderService.hide();
          } else {
            this.callGetOrderDetailDb(targetOrder);
          }
        },
        error: () => {
          this.callGetOrderDetailDb(targetOrder);
        },
      });
  }

  private callGetOrderDetailDb(targetOrder: string): void {
    this.apiService
      .getOrderDetail({ order_number: targetOrder, source: 'db' })
      .subscribe({
        next: (res: any) => {
          if (res?.success && res.data) {
            this.orderDetail = res.data;
            this.extractFromOrderDetail(res.data);
          }
          this.loaderService.hide();
        },
        error: () => {
          this.loaderService.hide();
        },
      });
  }

  private extractFromOrderDetail(data: any): void {
    if (!this.orderNumber) {
      this.orderNumber =
        data?.order_deatils?.order_number ||
        data?.order_detail?.order_number ||
        data?.order_invoice_list?.[0]?.order_number ||
        this.orderId;
    }
    if (!this.bookingNumber) {
      this.bookingNumber =
        data?.order_invoice_list?.[0]?.invoice_number ||
        data?.invoice_number ||
        '';
    }
    if (!this.deliveryAddress) {
      this.deliveryAddress =
        data?.shipping_address ||
        data?.order_deatils?.order_address?.[0] ||
        data?.order_invoice_list?.[0]?.order_address?.[0] ||
        null;
    }
    if (!this.items || this.items.length === 0) {
      this.items =
        data?.part_details ||
        data?.order_deatils?.order_lines ||
        data?.order_invoice_list?.[0]?.invoice_items ||
        [];
    }
    if (!this.paymentAmount) {
      this.paymentAmount =
        Number(data?.order_deatils?.order_amount) ||
        Number(data?.order_detail?.order_amount) ||
        0;
    }
  }

  formatPrice(amount: number | string | null | undefined): string {
    const num = Number(amount) || 0;
    return `₹${num.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    })}`;
  }

  gotoOrderDetails(): void {
    const orderData = {
      order_number: this.orderNumber || this.orderId,
      invoice_number: this.bookingNumber || this.invoiceId || '',
      booking_number: this.bookingNumber || '',
      document_type: 'PROFORMA',
    };

    if (orderData.order_number) {
      this.router.navigate(['/orders-details'], {
        state: {
          orderData,
          isBookingFlow: true,
          fromOrderConfirmation: true,
        },
      });
      return;
    }

    this.router.navigate(['/user-profile'], {
      queryParams: { section: 'orders' },
    });
  }

  gotoShopping(): void {
    this.router.navigate(['/product-list']);
  }

  retryPayment(): void {
    this.gotoOrderDetails();
  }
}
